import type { Dapp, Release } from '@src/dto';
import { LooseNftMetadata } from '../../../../types/api';
import {
  detectNftStorageProvider,
  getNftStorageProviderLabel,
} from '../../../../utils/nftStorageUtils';
import {
  extractFilename,
  getMetadataSolanaDappStore,
} from '../../../../utils/nftMetadataHelpers';
import {
  CopyableValue,
  Disclosure,
  Field,
  Section,
} from '../../../common/detailParts';
import { truncateHash } from '../../../common/truncateHash';

type NftInformationSolanaDappStore = {
  files?: Array<{
    purpose?: string;
    uri?: string;
  }>;
};

interface NftInformationSectionProps {
  release: Release;
  dapp: Dapp | null;
  nftMetadata: LooseNftMetadata | null;
}

export function NftInformationSection({
  release,
  dapp,
  nftMetadata,
}: NftInformationSectionProps) {
  if (!release.nftMintAddress && !dapp?.nftMintAddress) {
    return null;
  }

  const cluster = release.nftCluster || 'devnet';
  const providerInfo = detectNftStorageProvider(
    release.nftMetadataUri,
    release.uploadProvider,
    release.uploadFacilitator,
  );
  const metadataLabel = release.nftMetadataUri
    ? getNftStorageProviderLabel(release.nftMetadataUri, release.uploadProvider)
    : null;
  const solanaDappStore =
    getMetadataSolanaDappStore<NftInformationSolanaDappStore>(nftMetadata);
  const files = solanaDappStore?.files || [];
  const hasExtraStorageDetails =
    Boolean(release.uploadProviderId) ||
    files.length > 0 ||
    Boolean(nftMetadata?.image) ||
    Boolean(nftMetadata?.animation_url) ||
    Boolean(nftMetadata?.external_url);

  return (
    <Section
      title="On-chain"
      trailing={
        <span className="text-[11px] font-medium text-neutral-400 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/5">
          {providerInfo.name}
        </span>
      }
      className="mb-4"
    >
      <dl className="divide-y divide-white/5 -mx-2">
        {release.nftMintAddress && (
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-y-1 sm:gap-x-6 py-2.5 px-2 items-center">
            <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500">
              Mint
            </dt>
            <dd className="min-w-0">
              <CopyableValue
                value={release.nftMintAddress}
                display={truncateHash(release.nftMintAddress, 8, 8)}
                href={`https://explorer.solana.com/address/${release.nftMintAddress}?cluster=${cluster}`}
              />
            </dd>
          </div>
        )}

        {dapp?.nftMintAddress && (
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-y-1 sm:gap-x-6 py-2.5 px-2 items-center">
            <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500">
              App Collection
            </dt>
            <dd className="min-w-0">
              <CopyableValue
                value={dapp.nftMintAddress}
                display={truncateHash(dapp.nftMintAddress, 8, 8)}
                href={`https://explorer.solana.com/address/${dapp.nftMintAddress}?cluster=${cluster}`}
              />
            </dd>
          </div>
        )}

        {release.nftTransactionSignature && (
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-y-1 sm:gap-x-6 py-2.5 px-2 items-center">
            <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500">
              Transaction
            </dt>
            <dd className="min-w-0">
              <CopyableValue
                value={release.nftTransactionSignature}
                display={truncateHash(release.nftTransactionSignature, 8, 8)}
                href={`https://explorer.solana.com/tx/${release.nftTransactionSignature}?cluster=${cluster}`}
              />
            </dd>
          </div>
        )}

        {release.nftMetadataUri && (
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-y-1 sm:gap-x-6 py-2.5 px-2 items-center">
            <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500">
              Metadata
            </dt>
            <dd className="min-w-0">
              <CopyableValue
                value={release.nftMetadataUri}
                display={`View on ${metadataLabel ?? 'storage'}`}
                href={release.nftMetadataUri}
                mono={false}
              />
            </dd>
          </div>
        )}
      </dl>

      {hasExtraStorageDetails && (
        <Disclosure label="Storage details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Provider type">
              <span className="text-sm text-neutral-300">
                {providerInfo.type}
              </span>
            </Field>
            {release.uploadProviderId && (
              <Field label="Provider ID">
                <CopyableValue
                  value={release.uploadProviderId}
                  display={truncateHash(release.uploadProviderId, 12, 12)}
                />
              </Field>
            )}
            {(files.length > 0 ||
              nftMetadata?.image ||
              nftMetadata?.animation_url ||
              nftMetadata?.external_url) && (
              <div className="md:col-span-2">
                <Field label="NFT files">
                  <div className="flex flex-col gap-2">
                    {files.map((file, index) => (
                      <NftFileRow
                        key={`${file?.uri || 'file'}-${index}`}
                        label={file?.purpose || 'file'}
                        uri={file?.uri || ''}
                      />
                    ))}
                    {nftMetadata?.image && (
                      <NftFileRow label="Image" uri={nftMetadata.image} />
                    )}
                    {nftMetadata?.animation_url && (
                      <NftFileRow
                        label="Animation"
                        uri={nftMetadata.animation_url}
                      />
                    )}
                    {nftMetadata?.external_url && (
                      <NftFileRow
                        label="External"
                        uri={nftMetadata.external_url}
                      />
                    )}
                  </div>
                </Field>
              </div>
            )}
          </div>
        </Disclosure>
      )}
    </Section>
  );
}

function NftFileRow({ label, uri }: { label: string; uri: string }) {
  if (!uri) return null;
  return (
    <div className="flex items-center justify-between gap-3 min-w-0">
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500 capitalize min-w-[90px]">
        {label}
      </span>
      <div className="flex-1 min-w-0">
        <CopyableValue
          value={uri}
          display={extractFilename(uri)}
          href={uri}
          mono
        />
      </div>
    </div>
  );
}
